<template>
  <div>
    <div
      ref="mapWrapper"
      class="map-wrapper"
      @mousedown="startPan"
      @mousemove="panning"
      @mouseup="endPan"
      @mouseleave="endPan"
      @touchstart="startPan"
      @touchmove="panning"
      @touchend="endPan"
      @wheel="handleWheel"
    >
      <InlineSvg
        ref="map"
        :src="require('../../../assets/images/beige_map.svg')"
        @loaded="onSvgLoaded"
        @click="onMapClick"
      />

      <div class="map-header -yellow" v-if="zoomedLocation">
        <div class="title">
          {{ zoomedLocation.title }}
        </div>
        {{ humanizeType(zoomedLocation.location_type) }}
      </div>
      <Pin
        v-for="loc in locations"
        :key="loc.id"
        :location="loc"
        :scale="scale"
        :offset="offset"
        :min-x="vbMinX"
        :min-y="vbMinY"
        :active-location-id="zoomedLocation ? zoomedLocation.id : null"
      />
    </div>
  </div>
</template>

<script>
import InlineSvg from 'vue-inline-svg';
import Pin from './components/Pin.vue';
import Labels from './components/Labels.vue';

export default {
  name: 'MapInput',
  components: { InlineSvg, Pin, Labels },

  props: {
    locations: { type: Array, required: true },
    zoomedLocation: { type: Object, required: false },
  },

  data() {
    return {
      scale: 1,
      offset: { x: 0, y: 0 },
      coords: { x: 0, y: 0 },
      vbMinX: 0,
      vbMinY: 0,
      vbWidth: 1440,
      vbHeight: 800,
      originalViewBox: '0 0 1440 800',
      isPanning: false,
      startPoint: { x: 0, y: 0 },
      lastPinchDist: null,
    };
  },

  computed: {
    svg() {
      return this.$refs.map.$el;
    },
    wrapper() {
      return this.$refs.mapWrapper;
    },
  },

  mounted() {
    document.addEventListener('load', this.resolveScaleAndOffset);
    window.addEventListener('resize', this.resolveScaleAndOffset);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resolveScaleAndOffset);
  },

  methods: {
    updateViewBox() {
      if (!this.svg) return;
      const vbString = `${this.vbMinX} ${this.vbMinY} ${this.vbWidth} ${this.vbHeight}`;
      this.svg.setAttribute('viewBox', vbString);

      this.resolveScaleAndOffset();
    },

    resolveScaleAndOffset() {
      if (!this.svg || !this.wrapper) return;

      let wrapperBounds = this.wrapper.getBoundingClientRect();
      let mapBounds = this.svg.getBoundingClientRect();

      this.scale = this.vbWidth / mapBounds.width;

      this.offset = {
        x: mapBounds.left - wrapperBounds.left,
        y: mapBounds.top - wrapperBounds.top,
      };
    },

    onSvgLoaded() {
      const vbStr = this.svg.getAttribute('viewBox');
      if (vbStr) {
        this.originalViewBox = vbStr;
        const [x, y, w, h] = vbStr.split(' ').map(Number);
        this.vbMinX = x;
        this.vbMinY = y;
        this.vbWidth = w;
        this.vbHeight = h;
      }

      this.resolveScaleAndOffset();

      if (this.zoomedLocation) {
        this.zoomTo(this.zoomedLocation);
      }
    },

    getPoint(e) {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    },

    startPan(e) {
      if (window.innerWidth >= 768) return;
      if (e.touches && e.touches.length === 2) {
        this.handlePinchStart(e);
        return;
      }

      this.isPanning = true;
      this.startPoint = this.getPoint(e);
      this.wrapper.style.cursor = 'grabbing';
    },

    panning(e) {
      if (window.innerWidth >= 768) return;
      if (e.touches && e.touches.length === 2) {
        this.handlePinchMove(e);
        return;
      }

      if (!this.isPanning) return;
      e.preventDefault();

      const currentPoint = this.getPoint(e);
      const dxPx = this.startPoint.x - currentPoint.x;
      const dyPx = this.startPoint.y - currentPoint.y;
      this.vbMinX += dxPx * this.scale;
      this.vbMinY += dyPx * this.scale;

      this.updateViewBox();

      this.startPoint = currentPoint;
    },

    endPan() {
      this.isPanning = false;
      this.lastPinchDist = null;
      this.wrapper.style.cursor = '';
    },

    handleWheel(e) {
      if (window.innerWidth >= 768) return;

      e.preventDefault();

      const zoomSensitivity = 0.001;
      const zoomFactor = 1 + e.deltaY * zoomSensitivity;
      this.performZoom(zoomFactor, e.clientX, e.clientY);
    },
    handlePinchStart(e) {
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };
      this.lastPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    },

    handlePinchMove(e) {
      e.preventDefault();
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

      const currentDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

      if (this.lastPinchDist) {
        const zoomFactor = this.lastPinchDist / currentDist;

        const centerX = (p1.x + p2.x) / 2;
        const centerY = (p1.y + p2.y) / 2;

        this.performZoom(zoomFactor, centerX, centerY);
      }

      this.lastPinchDist = currentDist;
    },

    performZoom(factor, clientX, clientY) {
      const rect = this.svg.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const mouseInSVG_X = this.vbMinX + clickX * this.scale;
      const mouseInSVG_Y = this.vbMinY + clickY * this.scale;

      const oldWidth = this.vbWidth;
      const oldHeight = this.vbHeight;

      const newWidth = oldWidth * factor;
      const newHeight = oldHeight * factor;

      if (newWidth > 1440 * 1.5) return;
      if (newWidth < 100) return;

      this.vbWidth = newWidth;
      this.vbHeight = newHeight;

      this.vbMinX = mouseInSVG_X - clickX * (newWidth / rect.width);
      this.vbMinY = mouseInSVG_Y - clickY * (newHeight / rect.height);

      this.updateViewBox();
    },

    zoomTo(location) {
      const [oMinX, oMinY, oWidth, oHeight] = this.originalViewBox.split(' ').map(Number);

      const zoomFactor = 2;
      this.vbWidth = oWidth / zoomFactor;
      this.vbHeight = oHeight / zoomFactor;

      let newMinX = location.coords_x - this.vbWidth / 2;
      let newMinY = location.coords_y - this.vbHeight / 2;

      this.vbMinX = Math.max(oMinX, Math.min(newMinX, oMinX + oWidth - this.vbWidth));
      this.vbMinY = Math.max(oMinY, Math.min(newMinY, oMinY + oHeight - this.vbHeight));

      this.updateViewBox();
    },

    onMapClick(e) {
      let bound = this.svg.getBoundingClientRect();
      let x = Math.round((e.clientX - bound.left) * this.scale);
      let y = Math.round((e.clientY - bound.top) * this.scale);

      x += this.vbMinX;
      y += this.vbMinY;

      this.coords = { x, y };
    },

    humanizeType(typeString) {
      if (!typeString) return '';
      const spaced = typeString.replace(/_/g, ' ');
      return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.map-wrapper {
  @apply bg-dark-gray;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  overflow: hidden;
  cursor: default;
  touch-action: auto;

  svg {
    height: 100%;
    width: 100%;
  }

  @media (max-width: 768px) {
    touch-action: none;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
}

.map-svg-wrapper {
  position: relative;
}

.vector1,
.vector2 {
  position: absolute;
  left: 15%;
}

.vector1 {
  top: 2%;
}
.vector2 {
  bottom: 2%;
}
.map-header {
  position: absolute;
  left: 5%;
  top: 5%;
  display: inline-flex;
  padding: 12px 20px 12px 24px;
  align-items: center;
  border-left: 4px solid #fc443a;
  color: #000;
  font-family: Merriweather;
  font-size: clamp(18px, 2vw, 32px);
  font-style: normal;
  font-weight: 400;
  line-height: 1.3;
  background: #fff;
  max-width: min(90%, 600px);
  gap: 0.25rem;

  .title {
    font-size: clamp(28px, 5vw, 64px);
    line-height: 1.1;
  }

  &.-yellow {
    border-left: 4px solid #ffd647;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}

@media screen and (max-width: 768px) {
  .map-header {
    top: 10%;
    padding: 10px 16px 10px 20px;
    left: 5%;
    font-size: clamp(16px, 4vw, 22px);

    .title {
      font-size: clamp(22px, 7vw, 32px);
    }
  }
}

@media screen and (max-width: 480px) {
  .map-header {
    top: 8%;
    left: 4%;
    padding: 8px 14px 8px 18px;
    gap: 0.2rem;

    .title {
      font-size: clamp(20px, 8vw, 28px);
    }
  }
}

@media screen and (max-width: 768px) {
  .map-header {
    top: 15%;
  }
}
</style>
